import { useId } from 'react';
import { formatSiteRange, parseSiteRange, SiteRangeDraft, SiteScope } from '../lib/siteRange';

/**
 * "Sites" control for coupons: Any number of sites, or a range picked with a
 * dual-handle slider plus exact number inputs. Either end can be left open.
 */
export default function SiteRangeField({ value, onChange, scope }: {
  value: SiteRangeDraft;
  onChange: (next: SiteRangeDraft) => void;
  scope: SiteScope;
}) {
  const name = useId();
  const set = (patch: Partial<SiteRangeDraft>) => onChange({ ...value, ...patch });
  const lo = scope.minSites, hi = scope.maxSites;
  const clamp = (raw: string, fallback: number) => {
    const n = Math.round(Number(raw));
    return raw.trim() === '' || !Number.isFinite(n) ? fallback : Math.min(hi, Math.max(lo, n));
  };
  const minPos = value.minOpen ? lo : clamp(value.min, lo);
  const maxPos = value.maxOpen ? hi : clamp(value.max, hi);
  const pct = (n: number) => (hi === lo ? 0 : ((n - lo) / (hi - lo)) * 100);
  const parsed = parseSiteRange(value, scope);

  return (
    <div className="field site-range">
      <span>Sites (Agency plan)</span>
      <div className="site-range-modes" role="radiogroup" aria-label="Sites">
        <label className="check-row">
          <input type="radio" name={name} checked={value.mode === 'any'}
            onChange={() => set({ mode: 'any' })} />
          <span>Any number of sites</span>
        </label>
        <label className="check-row">
          <input type="radio" name={name} checked={value.mode === 'range'}
            onChange={() => set({ mode: 'range' })} />
          <span>A range of sites</span>
        </label>
      </div>

      {value.mode === 'range' && (
        <>
          <div className="range-dual">
            <div className="range-dual-track" />
            <div className="range-dual-fill"
              style={{ left: `${pct(minPos)}%`, width: `${Math.max(0, pct(maxPos) - pct(minPos))}%` }} />
            <input type="range" aria-label="Minimum sites slider" min={lo} max={hi} step={1}
              value={minPos} disabled={value.minOpen}
              onChange={(e) => set({ min: String(Math.min(Number(e.target.value), maxPos)) })} />
            <input type="range" aria-label="Maximum sites slider" min={lo} max={hi} step={1}
              value={maxPos} disabled={value.maxOpen}
              onChange={(e) => set({ max: String(Math.max(Number(e.target.value), minPos)) })} />
          </div>
          <div className="site-range-ends">
            <div className="site-range-end">
              <input type="number" aria-label="Minimum sites" min={lo} max={hi} step={1}
                value={value.minOpen ? '' : value.min} disabled={value.minOpen}
                placeholder="No minimum" onChange={(e) => set({ min: e.target.value })} />
              <label className="check-row">
                <input type="checkbox" checked={value.minOpen}
                  onChange={(e) => set({ minOpen: e.target.checked })} />
                <span>No minimum</span>
              </label>
            </div>
            <span className="muted">to</span>
            <div className="site-range-end">
              <input type="number" aria-label="Maximum sites" min={lo} max={hi} step={1}
                value={value.maxOpen ? '' : value.max} disabled={value.maxOpen}
                placeholder="No maximum" onChange={(e) => set({ max: e.target.value })} />
              <label className="check-row">
                <input type="checkbox" checked={value.maxOpen}
                  onChange={(e) => set({ maxOpen: e.target.checked })} />
                <span>No maximum</span>
              </label>
            </div>
          </div>
        </>
      )}

      {parsed.ok ? (
        <span className="muted small" data-testid="site-range-summary">
          {parsed.minSites === null && parsed.maxSites === null
            ? 'Valid for any number of sites.'
            : `Valid for ${formatSiteRange(parsed.minSites, parsed.maxSites).replace(/^Up to/, 'up to')} sites.`}
          {' '}Applies to new Agency subscriptions only.
        </span>
      ) : (
        <span className="field-error small" role="alert">{parsed.error}</span>
      )}
    </div>
  );
}
